import { useAuth } from 'context/auth';
import { useConfirmModal } from 'context/confirmModal';
import { Account } from 'crono-fe-common/types/account';
import { Opportunity } from 'crono-fe-common/types/DTO/opportunityViewResponseDTO';
import IntegrationType from 'crono-fe-common/types/enums/integrationType';
import { Event, MeetingActivity } from 'crono-fe-common/types/event';
import { withMeta } from 'crono-fe-common/types/mutationMeta';
import { Prospect } from 'crono-fe-common/types/prospect';
import useDeleteMeeting from 'hooks/services/calendar/useDeleteMeeting';
import useGetCalendar from 'hooks/services/calendar/useGetCalendar';
import { useDeleteEvent } from 'hooks/services/event/useDeleteEvent';
import { useDeleteInCrmPreference } from 'hooks/useDeleteInCrmPreference';
import { MeetingUpsertModal } from 'pages/accountDrawer/meetingUpsertModal';
import { RescheduleMeeting } from 'pages/accountDrawer/meetingView/RescheduleMeeting';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';

// What the upsert modal is currently opened on: an existing meeting (edit) or
// a blank form optionally prefilled with an account and a contact (creation).
interface MeetingUpsertTarget {
  eventId?: number;
  account?: Account;
  prospect?: Prospect;
  opportunity?: Opportunity;
}

interface MeetingActionsContextValue {
  openMeetingCreator: (
    account?: Account,
    prospect?: Prospect,
    opportunity?: Opportunity,
  ) => void;
  openMeetingEditor: (eventId: number) => void;
  openMeetingReschedule: (meetingInfo: MeetingActivity) => void;
  requestMeetingDelete: (event: Event) => void;
  calendarEmails: (string | null)[];
}

const MeetingActionsContext = createContext<MeetingActionsContextValue | null>(
  null,
);

// The value is memoized and nothing in it changes after the calendar query
// resolves, so a row never re-renders because another one opened a modal.
export const useMeetingActionsContext = () => {
  const context = useContext(MeetingActionsContext);
  if (!context) {
    throw new Error(
      'useMeetingActionsContext must be used within MeetingActionsProvider',
    );
  }

  return context;
};

interface MeetingActionsProviderProps {
  onMeetingDeleted?: () => void;
}

// Hosts the meeting editor, the reschedule dialog and the delete flow once for
// the whole view, so a list of meetings mounts one of each rather than one per
// row — and runs the calendar query once instead of per row.
export const MeetingActionsProvider = ({
  onMeetingDeleted,
  children,
}: PropsWithChildren<MeetingActionsProviderProps>) => {
  const { user } = useAuth();
  const { openModal } = useConfirmModal();
  const { data: calendarsData } = useGetCalendar();
  const { mutate: deleteMeeting } = useDeleteMeeting();
  const { mutate: deleteEvent } = useDeleteEvent();
  const { deleteInCrmPreference, saveDeleteInCrmPreference } =
    useDeleteInCrmPreference();

  const integrationType = user?.company?.integrationType;
  const isCronoOnly = integrationType === IntegrationType.CRONO;

  const [upsertTarget, setUpsertTarget] = useState<MeetingUpsertTarget | null>(
    null,
  );
  const [isUpsertOpen, setIsUpsertOpen] = useState(false);
  const [rescheduledMeeting, setRescheduledMeeting] =
    useState<MeetingActivity | null>(null);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);

  const value = useMemo<MeetingActionsContextValue>(
    () => ({
      calendarEmails: (calendarsData?.data?.data ?? []).map(
        (calendar) => calendar.email,
      ),
      openMeetingCreator: (account, prospect, opportunity) => {
        setUpsertTarget({ account, prospect, opportunity });
        setIsUpsertOpen(true);
      },
      openMeetingEditor: (eventId) => {
        setUpsertTarget({ eventId });
        setIsUpsertOpen(true);
      },
      openMeetingReschedule: (meetingInfo) => {
        setRescheduledMeeting(meetingInfo);
        setIsRescheduleOpen(true);
      },
      requestMeetingDelete: (event) => {
        const calendarMeeting = event.meeting;
        const showDeleteInCrmCheckbox = !!event.isSynced && !isCronoOnly;

        openModal({
          title: 'Are you sure you want to delete this meeting?',
          text: 'The meeting will be permanently deleted',
          confirmText: 'Yes',
          cancelText: 'No',
          showCheckbox: showDeleteInCrmCheckbox,
          checkboxDefaultValue: deleteInCrmPreference,
          checkboxText: 'Delete also in CRM',
          confirmFunction: (checkboxValue) => {
            const deleteInCrm = showDeleteInCrmCheckbox && !!checkboxValue;
            if (showDeleteInCrmCheckbox) {
              saveDeleteInCrmPreference(deleteInCrm);
            }

            const deleteCronoEvent = () => {
              deleteEvent(
                { eventId: event.id, deleteInCrm },
                { onSuccess: onMeetingDeleted },
              );
            };

            // Every meeting goes through the event endpoint — the only one that
            // can also drop the CRM copy. A calendar-backed one needs its
            // calendar entry cleared too, which that endpoint doesn't do, so the
            // calendar goes first: the Crono event is deleted only once the entry
            // it mirrors is gone, and the event's toast is the single one shown.
            if (calendarMeeting) {
              deleteMeeting(
                withMeta(
                  {
                    meetingId: calendarMeeting.id,
                    provider: calendarMeeting.provider,
                  },
                  { silentSuccess: true },
                ),
                { onSuccess: deleteCronoEvent },
              );
              return;
            }

            deleteCronoEvent();
          },
        });
      },
    }),
    [
      calendarsData,
      openModal,
      deleteMeeting,
      deleteEvent,
      onMeetingDeleted,
      isCronoOnly,
      deleteInCrmPreference,
      saveDeleteInCrmPreference,
    ],
  );

  return (
    <MeetingActionsContext.Provider value={value}>
      {children}

      {/* Both stay mounted once opened so the dialog can animate out, but neither is
      mounted before that */}
      {upsertTarget && (
        <MeetingUpsertModal
          open={isUpsertOpen}
          onOpenChange={setIsUpsertOpen}
          eventId={upsertTarget.eventId}
          initialAccount={upsertTarget.account}
          initialProspect={upsertTarget.prospect}
          initialOpportunity={upsertTarget.opportunity}
        />
      )}

      {rescheduledMeeting && (
        <RescheduleMeeting
          key={rescheduledMeeting.id}
          open={isRescheduleOpen}
          onOpenChange={setIsRescheduleOpen}
          meetingId={rescheduledMeeting.id}
          provider={rescheduledMeeting.provider}
          initialStartDate={rescheduledMeeting.startTime}
          initialEndDate={rescheduledMeeting.endTime}
          initialIsAllDay={rescheduledMeeting.isAllDay}
        />
      )}
    </MeetingActionsContext.Provider>
  );
};
